import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Always show error UI — a blank page is worse than a reload button.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Skip logging noisy third-party errors
    const msg = error.message || '';
    if (!msg.includes('Script error') && !msg.includes('spotify')) {
      console.error('Error caught by boundary:', error, errorInfo);
      console.error('Component stack:', errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message ?? '';
      const isChunkError =
        this.state.error?.name === 'ChunkLoadError' ||
        msg.includes('dynamically imported module');

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center p-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {isChunkError ? 'A new version is available' : 'Something went wrong'}
            </h2>
            {isChunkError && (
              <p className="text-gray-500 dark:text-gray-400 mb-4 text-sm">
                YapZap was updated. Reload to get the latest version.
              </p>
            )}
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
